// Web capability gating. The capability MODEL (list, persona predicates,
// playlist-capability set) comes from @workspace/capabilities (CB-140); this
// file only adds web-specific route/landing helpers over WorkspaceWithRole.
import {
  isControlPrimaryCapabilities,
  PLAYLIST_MANAGE_CAPABILITIES,
} from "@workspace/capabilities";
import type {
  WorkspaceWithRole,
  Capability,
  ChannelType,
} from "@workspace/api-client-react";

export type { Capability };

export function workspaceHasCapability(
  workspace: WorkspaceWithRole | null | undefined,
  capability: Capability,
): boolean {
  return workspace?.capabilities?.includes(capability) ?? false;
}

/** First allowed admin route for capability-based redirects. */
export function defaultAppPath(
  workspace: WorkspaceWithRole | null | undefined,
): string {
  if (!workspace) return "/workspaces";
  if (workspaceHasCapability(workspace, "view.dashboard")) return "/dashboard";
  if (workspaceHasCapability(workspace, "control.devices")) {
    return "/control-selection";
  }
  if (
    workspaceHasCapability(workspace, "manage.fleet") ||
    workspaceHasCapability(workspace, "manage.locations")
  )
    return "/sites";
  if (workspaceHasCapability(workspace, "manage.media")) return "/library";
  return "/start";
}

export function isControlPrimary(
  workspace: WorkspaceWithRole | null | undefined,
): boolean {
  if (!workspace) return false;
  return isControlPrimaryCapabilities(workspace.capabilities ?? []);
}

export const isControlOnlyUser = isControlPrimary;

export function isOperatorOnly(workspaces: WorkspaceWithRole[]): boolean {
  return workspaces.length > 0 && workspaces.every((w) => isControlPrimary(w));
}

export function canManageFleet(
  workspace: WorkspaceWithRole | null | undefined,
): boolean {
  return workspaceHasCapability(workspace, "manage.fleet");
}

export function canManageContent(
  workspace: WorkspaceWithRole | null | undefined,
): boolean {
  return (
    workspaceHasCapability(workspace, "manage.media") ||
    PLAYLIST_MANAGE_CAPABILITIES.some((c) =>
      workspaceHasCapability(workspace, c),
    ) ||
    workspaceHasCapability(workspace, "manage.schedules")
  );
}

const PATH_CAPABILITY: Record<string, Capability | Capability[] | null> = {
  "/dashboard": "view.dashboard",
  "/fleet": "manage.fleet",
  "/locations": "manage.locations",
  "/sites": ["manage.locations", "manage.fleet"],
  "/schedules": "manage.schedules",
  "/audiences": "manage.schedules",
  "/library": "manage.media",
  "/playlists": [...PLAYLIST_MANAGE_CAPABILITIES],
  "/scheduling": "manage.schedules",
  "/logs": "view.logs", // legacy — redirects to /reports
  "/reports": "view.logs",
  "/team": "manage.members",
  "/ad-rental": "manage.ad_inventory",
  "/billing": "view.billing",
  "/settings": "manage.settings",
  "/support": null,
  // Getting an installer is not a privileged act; every member may reach it.
  "/install": null,
  "/onboarding": "manage.settings",
  "/control-selection": "control.devices",
};

export function canAccessPath(
  workspace: WorkspaceWithRole | null | undefined,
  path: string,
): boolean {
  if (!workspace) return false;
  const base = path.split("?")[0] ?? path;
  if (base.startsWith("/control/")) {
    return workspaceHasCapability(workspace, "control.devices");
  }
  if (base.startsWith("/fleet/")) {
    return workspaceHasCapability(workspace, "manage.fleet");
  }
  if (base.startsWith("/playlists/")) {
    return PLAYLIST_MANAGE_CAPABILITIES.some((c) =>
      workspaceHasCapability(workspace, c),
    );
  }
  if (base.startsWith("/scheduling/")) {
    return workspaceHasCapability(workspace, "manage.schedules");
  }

  for (const [prefix, cap] of Object.entries(PATH_CAPABILITY)) {
    if (base === prefix || base.startsWith(`${prefix}/`)) {
      if (cap === null) return true;
      if (Array.isArray(cap)) {
        return cap.some((c) => workspaceHasCapability(workspace, c));
      }
      return workspaceHasCapability(workspace, cap);
    }
  }
  return true;
}

export const canAccessRoute = canAccessPath;
export const defaultLandingPath = defaultAppPath;

/** Permission-group channel scope: empty list means all channels. */
export function scopeAllowsChannel(
  workspace: WorkspaceWithRole | null | undefined,
  channel: ChannelType,
): boolean {
  if (!workspace) return false;
  const scoped = workspace.scopes?.channelTypes;
  if (!scoped || scoped.length === 0) return true;
  return scoped.includes(channel);
}
